import React, { useCallback, useEffect, useMemo, useState } from "react";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel.js";
import { BaseDraggable } from "../BaseDraggable";
import { useStore } from "../../../stores/store";
import { DraggableKeys } from "../../../interfaces/config";
import markupButtons, { MarkupButton } from "./markupButtons";
import getPointSymbol from "./getPointSymbol";
import getLineSymbol from "./getLineSymbol";
import getPolygonSymbol from "./getPolygonSymbol";
import getTextSymbol from "./getTextSymbol";
import DeleteContextMenu from "./DeleteContextMenu";

const key = DraggableKeys.MARKUP_DRAGGABLE;

export default function MarkupDraggable() {
  const {
    isActive,
    toggleActiveItem,
    setTooltipText,
    mapView,
    markupActiveTool,
    setMarkupActiveTool,
    markupLayer,
    markupPointColor,
    markupPointSize,
    markupPointStyle,
    markupPointOutlineColor,
    markupPointOutlineWidth,
    markupPointOutlineStyle,
    markupLineColor,
    markupLineStyle,
    markupLineWidth,
    markupPolygonOutlineColor,
    markupPolygonOutlineStyle,
    markupPolygonOutlineWidth,
    markupFillColor,
    markupFillStyle,
    markupText,
    markupFontColor,
    markupFontFamily,
    markupFontSize,
    markupFontStyle,
    markupFontWeight,
  } = useStore();

  const sketchVM = useMemo(() => {
    if (mapView && markupLayer) {
      return new SketchViewModel({
        view: mapView,
        layer: markupLayer,
        updateOnGraphicClick: false,
      });
    }
  }, [mapView]);

  const [contextMenuShown, setContextMenuShown] = useState(false);
  const [contextX, setContextX] = useState(0);
  const [contextY, setContextY] = useState(0);
  const [activeGraphic, setActiveGraphic] = useState<__esri.Graphic | null>(
    null
  );

  const buttons = useMemo<MarkupButton[]>(() => markupButtons, []);
  const activeButton = useMemo(() => {
    return buttons.find((button) => button.value === markupActiveTool);
  }, [markupActiveTool]);

  const removeGraphic = useCallback(() => {
    if (activeGraphic) {
      markupLayer?.remove(activeGraphic);
      setActiveGraphic(null);
    }
  }, [activeGraphic, markupLayer]);

  useEffect(() => {
    if (activeButton?.value !== "point" || !sketchVM) return;
    const pointSymbol = getPointSymbol({
      markupPointColor,
      markupPointSize,
      markupPointStyle,
      markupPointOutlineColor,
      markupPointOutlineWidth,
      markupPointOutlineStyle,
    });

    sketchVM.pointSymbol = pointSymbol;
  }, [
    markupPointColor,
    markupPointSize,
    markupPointStyle,
    markupPointOutlineColor,
    markupPointOutlineWidth,
    markupPointOutlineStyle,
    activeButton,
    sketchVM,
  ]);

  useEffect(() => {
    if (activeButton?.value !== "text" || !sketchVM) return;
    const textSymbol = getTextSymbol({
      markupText,
      markupFontColor,
      markupFontFamily,
      markupFontSize,
      markupFontStyle,
      markupFontWeight,
    });
    sketchVM.pointSymbol = textSymbol;
  }, [
    markupText,
    markupFontColor,
    markupFontFamily,
    markupFontSize,
    markupFontStyle,
    markupFontWeight,
    activeButton,
    sketchVM,
  ]);

  useEffect(() => {
    if (sketchVM) {
      const lineSymbol = getLineSymbol({
        markupLineColor,
        markupLineStyle,
        markupLineWidth,
      });
      sketchVM.polylineSymbol = lineSymbol;
    }
  }, [markupLineColor, markupLineStyle, markupLineWidth, sketchVM]);

  useEffect(() => {
    if (sketchVM) {
      const polygonSymbol = getPolygonSymbol({
        markupPolygonOutlineColor,
        markupPolygonOutlineStyle,
        markupPolygonOutlineWidth,
        markupFillColor,
        markupFillStyle,
      });
      sketchVM.polygonSymbol = polygonSymbol;
    }
  }, [
    markupPolygonOutlineColor,
    markupPolygonOutlineStyle,
    markupPolygonOutlineWidth,
    markupFillColor,
    markupFillStyle,
    sketchVM,
  ]);

  useEffect(() => {
    if (!sketchVM) return;
    sketchVM.updateOnGraphicClick = markupActiveTool === "edit";
  }, [sketchVM, markupActiveTool]);

  useEffect(() => {
    if (!markupLayer || !mapView) return;
    let handle: __esri.Handle;

    if (markupActiveTool === "delete") {
      handle = mapView.on("click", async (e: any) => {
        if (e.button === 2) {
          const res = (await mapView.hitTest(e)) as any;
          if (res.results.length > 0) {
            setContextX(e.native.clientX);
            setContextY(e.native.clientY);
            setActiveGraphic(res.results[0].graphic);
            setContextMenuShown(true);
          }
        }
      });
    }
    return () => {
      handle?.remove();
    };
  }, [mapView, markupLayer, markupActiveTool]);

  const toggleActiveMarkupTool = useCallback(
    (tool: string) => {
      if (markupActiveTool === tool) {
        setMarkupActiveTool("");
      } else {
        setMarkupActiveTool(tool);
      }
    },
    [markupActiveTool, setMarkupActiveTool]
  );

  useEffect(() => {
    if (!sketchVM) return;
    if (markupActiveTool === "" || activeButton?.drawType === "eraser") {
      sketchVM.cancel();
      setTooltipText("");
      if (activeButton?.drawType === "eraser") {
        markupLayer?.removeAll();
      }
    } else {
      if (activeButton?.startDraw !== false) {
        sketchVM?.create(activeButton?.drawType);
        setTooltipText(activeButton?.labels?.start || "");
        sketchVM?.on("create", (e: any) => {
          if (e.state === "complete") {
            sketchVM?.create(activeButton?.drawType);
            setTooltipText(activeButton?.labels?.start || "");
          }
          if (e.state === "start" || e.state === "active") {
            setTooltipText(activeButton?.labels?.active || "");
          }
        });
      }
    }
  }, [markupActiveTool, activeButton, setTooltipText, sketchVM]);

  return (
    <>
      <BaseDraggable
        id={key}
        onClose={() => toggleActiveItem(key)}
        visible={isActive(key)}
        initialPosition="top-right"
        headerText="MARKUP TOOLS"
        size="sm"
      >
        <div className="">
          <div className="flex items-center justify-between flex-wrap">
            {buttons.map((button) => (
              <button
                title={button.label}
                className={`btn-athena w-9 h-9 p-1 m-1 ${button.value === markupActiveTool &&
                  markupActiveTool !== "eraser"
                  ? "active"
                  : ""
                  }`}
                key={button.value}
                onClick={() => toggleActiveMarkupTool(button.value)}
              >
                <img src={button.icon} alt={button.label} />
              </button>
            ))}
          </div>
          <hr className="my-4" />
          {activeButton && (
            <div className="max-h-10 overflow-auto md:max-h-[40vh]">
              {activeButton.component && <activeButton.component />}
            </div>
          )}
        </div>
      </BaseDraggable>
      {contextMenuShown && (
        <DeleteContextMenu
          x={contextX}
          y={contextY}
          onClose={() => setContextMenuShown(false)}
          onDelete={() => {
            removeGraphic();
            setContextMenuShown(false);
          }}
        />
      )}
    </>
  );
}
